import type { CSSProperties } from "react";

/**
 * Where a whole class stands on one objective, as three parts of one bar.
 *
 * Three because there are three states and no fourth, and every child is in exactly one of them:
 * learnt it, working on it, has not started. A single percentage would be a number with no referent
 * — there is no sixtieth of an idea — and it would also hide the distinction a teacher acts on,
 * since a child who has not started and a child who is stuck are different problems with different
 * answers.
 *
 * **The widths are measurements, so they are inline styles, so this is its own file.** That is the
 * same arrangement `level-bar.tsx` uses and for the same reason: a width computed from a class's
 * data has no static form and cannot be a Tailwind class, `check-styles.ts` refuses inline styles
 * everywhere else, and the allowance is granted per file by name. The object comes from a named
 * helper rather than being written at the call site, because a function that can only return a
 * percentage cannot smuggle a colour or a spacing decision in beside it.
 *
 * The remainder is not drawn: the track's own background IS "not started", so the two coloured
 * segments and the gap they leave account for the class exactly.
 */
const spanWidth = (percent: number): CSSProperties =>
  ({ "--span-w": `${percent.toFixed(1)}%` }) as CSSProperties;

export function ClassBar({
  learnt,
  practising,
  total,
}: {
  learnt: number;
  practising: number;
  total: number;
}) {
  // Guarded: an empty class would divide by nought, and a class whose rows outnumbered it — which
  // would mean a stale grid — would draw past the end of the track rather than saying so.
  const of = (n: number) => (total > 0 ? Math.max(0, Math.min(1, n / total)) * 100 : 0);

  return (
    <span className="flex h-3 w-full overflow-hidden rounded-pill bg-edge">
      <span className="w-span block h-full bg-correct" style={spanWidth(of(learnt))} />
      <span className="w-span block h-full bg-sky" style={spanWidth(of(practising))} />
    </span>
  );
}
